import React from "react";
import { useNavigate } from "react-router-dom";
import lockicon from "../../assets/images/lock_outline.svg";
import newpolicyicon from "../../assets/images/newpolicydark.svg";
import Button from "../../components/common/Button/Button";
import useLanguageTranslate from "../../hooks/useLanguageTranslate";

export const PLACEHOLDER_SIGNED_OUT = "signedOut";
export const PLACEHOLDER_NO_POLICIES = "noPolicies";

/**
 * Stands in for the policy cards when there are none to show, in the two cases
 * where that is expected rather than an error:
 *
 *  - `signedOut` — the visitor is not logged in. The page is reachable without
 *    an account now, so it has to say why it is empty and offer the way in.
 *  - `noPolicies` — logged in, the request has come back, and nothing active
 *    came with it. Nothing is wrong; the useful thing to offer is a policy.
 *
 * Sized and bordered like a PolicyCard — same 20px radius, same #DDE2E4 edge,
 * same #F6F8F9 fill — so it reads as an item in the grid rather than a notice
 * pasted over it. It spans the row because a card-width block would sit in the
 * corner of an otherwise blank area.
 */
const PoliciesPlaceholder = ({ variant }) => {
  const navigate = useNavigate();
  const translate = useLanguageTranslate();

  const isSignedOut = variant === PLACEHOLDER_SIGNED_OUT;

  const content = isSignedOut
    ? {
        icon: lockicon,
        title: translate(
          "Sign in to view your policies",
          "سجّل الدخول لعرض وثائقك"
        ),
        body: translate(
          "Your active policies, renewals and documents are all here once you sign in.",
          "ستجد هنا وثائقك السارية وتجديداتها ومستنداتها بمجرد تسجيل الدخول."
        ),
        // Matching the wording the rest of the app uses for this action.
        cta: translate("Sign in", "تسجيل الدخول"),
        onClick: () => navigate("/sign-in"),
      }
    : {
        icon: newpolicyicon,
        title: translate("You have no active policies", "لا توجد لديك وثائق سارية"),
        body: translate(
          "Get covered in minutes. Browse our products and issue your first policy online.",
          "احصل على التغطية في دقائق. تصفّح منتجاتنا وأصدر وثيقتك الأولى عبر الإنترنت."
        ),
        cta: translate("Get a new policy", "احصل على وثيقة جديدة"),
        onClick: () => navigate("/new-policy"),
      };

  return (
    <div className="policies-placeholder w-full flex flex-col items-center justify-center text-center rounded-[20px] border border-solid border-[#DDE2E4] bg-[#F6F8F9] px-6 py-12 max767:px-5 max767:py-10">
      <span className="flex w-14 h-14 items-center justify-center rounded-full border border-solid border-[#DDE2E4] bg-white">
        <img src={content.icon} alt="" aria-hidden="true" className="w-6 h-6" />
      </span>

      <h2 className="mt-4 text-[#1A2024] text-lg not-italic font-semibold leading-6 tracking-[-0.18px]">
        {content.title}
      </h2>

      <p className="mt-2 max-w-[46ch] text-[#5B6871] text-sm not-italic font-normal leading-[22px] tracking-[-0.14px]">
        {content.body}
      </p>

      {/* The app's CTA shape — 10px radius on the #55C6EF the Button carries
          by default — at the 48px height the policy flows use for their
          sticky button on a phone, rather than the 56px of `.primary-button`.
          That class is sized for a form submit at the foot of a page; this is
          one line of a placeholder, and at 56px full-width it read as the
          loudest thing on an otherwise empty screen. */}
      <div className="mt-6 w-full max-w-[280px] max767:max-w-full">
        <Button
          label={content.cta}
          onSubmit={content.onClick}
          fontSize="text-base max767:text-sm"
          className="w-full rounded-[10px] px-5 py-3"
        />
      </div>
    </div>
  );
};

export default PoliciesPlaceholder;
